import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { socialApi } from '@/api';
import { Button } from '@/components/ui/Button';
import { ConnectionStatusBadge, TestStatusBadge } from './ConnectionStatusBadge';
import type { PlatformSetupGuide } from '../config/platformSetupGuides';
import type { SocialAccountResponse, SocialConnectionTestResult } from '@/types/social';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';
import { getPlatformById } from '../../config/platformCapabilities';

const WIZARD_PROGRESS_KEY = 'social.wizard.progress';

function loadProgress(platformId: string): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(`${WIZARD_PROGRESS_KEY}.${platformId}`);
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
  } catch {
    return {};
  }
}

function saveProgress(platformId: string, stepId: string, done: boolean) {
  const prev = loadProgress(platformId);
  prev[stepId] = done;
  localStorage.setItem(`${WIZARD_PROGRESS_KEY}.${platformId}`, JSON.stringify(prev));
}

interface PlatformSetupWizardProps {
  guide: PlatformSetupGuide;
  accounts: SocialAccountResponse[];
  onRefresh: () => void;
}

export function PlatformSetupWizard({ guide, accounts, onRefresh }: PlatformSetupWizardProps) {
  const navigate = useNavigate();
  const platform = getPlatformById(guide.platformId);
  const [stepIndex, setStepIndex] = useState(0);
  const [progress, setProgress] = useState(() => loadProgress(guide.platformId));
  const [tests, setTests] = useState<SocialConnectionTestResult[]>([]);
  const [testing, setTesting] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const account = accounts.find(
    (a) => a.platform.toUpperCase() === (platform?.apiPlatform ?? guide.platformId.toUpperCase()),
  );

  const steps = guide.steps;
  const current = steps[stepIndex];
  const completedCount = steps.filter((s) => progress[s.id]).length;

  const markDone = (id: string) => {
    saveProgress(guide.platformId, id, true);
    setProgress({ ...progress, [id]: true });
  };

  const addChannelAndConnect = async () => {
    setConnecting(true);
    try {
      let acc = account;
      if (!acc) {
        acc = await socialApi.connectAccount({
          platform: platform?.apiPlatform ?? guide.platformId.toUpperCase(),
          accountName: `${guide.label} channel`,
          accountId: `pending_${Date.now()}`,
        });
      }
      const oauth = await socialApi.getOAuthUrl(acc.id);
      if (oauth.url.includes('/oauth/demo/')) {
        await socialApi.demoConnect(acc.id);
        notify.success('Connected in demo mode — run tests below');
        markDone('oauth');
        onRefresh();
      } else {
        window.location.href = oauth.url;
      }
    } catch (err) {
      showApiError(err, 'Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  const runTests = async () => {
    if (!account?.id) {
      notify.error('Connect channel first');
      return;
    }
    setTesting(true);
    try {
      const results = await socialApi.runConnectionTests(account.id);
      setTests(results);
      if (results.every((t) => t.status === 'PASSED')) {
        markDone('validate');
        notify.success('All tests passed');
      } else {
        notify.info('Some tests need attention — see fix actions');
      }
    } catch (err) {
      showApiError(err, 'Tests failed');
    } finally {
      setTesting(false);
    }
  };

  const progressPct = useMemo(
    () => Math.round((completedCount / Math.max(steps.length, 1)) * 100),
    [completedCount, steps.length],
  );

  return (
    <div className="grid gap-6 lg:grid-cols-12">
      <aside className="card lg:col-span-4 max-h-[80vh] overflow-y-auto p-3">
        <p className="mb-2 text-xs font-semibold uppercase text-slate-500">Setup progress {progressPct}%</p>
        <ol className="space-y-1">
          {steps.map((s, i) => (
            <li key={s.id}>
              <button
                type="button"
                className={`flex w-full items-start gap-2 rounded-lg p-2 text-left text-sm ${
                  i === stepIndex ? 'bg-primary-50 dark:bg-primary-950/40' : 'hover:bg-slate-50 dark:hover:bg-slate-800'
                }`}
                onClick={() => setStepIndex(i)}
              >
                {progress[s.id] ? (
                  <CheckCircleIcon className="shrink-0 text-emerald-600" fontSize="small" />
                ) : (
                  <RadioButtonUncheckedIcon className="shrink-0 text-slate-400" fontSize="small" />
                )}
                <span>{s.title}</span>
              </button>
            </li>
          ))}
        </ol>
      </aside>

      <section className="card lg:col-span-8 p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold">{current.title}</h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">{current.description}</p>
          </div>
          {account?.connected && <ConnectionStatusBadge score={98} status="HEALTHY" />}
        </div>

        <ul className="mb-6 space-y-2">
          {current.checklist.map((item) => (
            <li key={item} className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={!!progress[`${current.id}:${item}`]}
                onChange={(e) => {
                  const key = `${current.id}:${item}`;
                  saveProgress(guide.platformId, key, e.target.checked);
                  setProgress({ ...progress, [key]: e.target.checked });
                }}
              />
              {item}
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap gap-2">
          {current.id === 'oauth' && (
            <Button onClick={() => void addChannelAndConnect()} loading={connecting}>
              {account?.connected ? 'Reconnect OAuth' : 'Connect now'}
            </Button>
          )}
          {(current.id === 'validate' || current.id.startsWith('test_')) && (
            <Button variant="secondary" onClick={() => void runTests()} loading={testing}>
              Run all connection tests
            </Button>
          )}
          {current.helpUrl && (
            <a href={current.helpUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-primary-600">
              Developer docs <OpenInNewIcon sx={{ fontSize: 14 }} />
            </a>
          )}
          <a
            href={guide.developerPortalUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-sm text-slate-600"
          >
            Open {guide.label} portal <OpenInNewIcon sx={{ fontSize: 14 }} />
          </a>
          <Button
            variant="secondary"
            onClick={() => {
              markDone(current.id);
              if (stepIndex < steps.length - 1) setStepIndex(stepIndex + 1);
            }}
          >
            Mark step complete
          </Button>
        </div>

        {tests.length > 0 && (
          <div className="mt-6 border-t pt-4 dark:border-slate-700">
            <h3 className="mb-2 text-sm font-semibold">Test results</h3>
            <ul className="space-y-2">
              {tests.map((t) => (
                <li key={t.testType} className="rounded-lg border p-3 text-sm dark:border-slate-700">
                  <div className="flex justify-between gap-2">
                    <span className="font-medium">{t.testType}</span>
                    <TestStatusBadge status={t.status} />
                  </div>
                  <p className="text-slate-600 dark:text-slate-400">{t.message}</p>
                  {t.fixAction && (
                    <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">Fix: {t.fixAction}</p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {guide.unsupportedFeatures.length > 0 && (
          <p className="mt-4 text-xs text-slate-500">
            Not supported: {guide.unsupportedFeatures.join(', ')}
          </p>
        )}

        <div className="mt-6 flex justify-between">
          <Button variant="secondary" disabled={stepIndex === 0} onClick={() => setStepIndex(stepIndex - 1)}>
            Previous
          </Button>
          <Button
            variant="secondary"
            disabled={stepIndex >= steps.length - 1}
            onClick={() => setStepIndex(stepIndex + 1)}
          >
            Next step
          </Button>
        </div>
      </section>

      <div className="lg:col-span-12">
        <Link to="/social/connect" className="text-sm text-primary-600 hover:underline">
          ← Back to Connection Center
        </Link>
        {' · '}
        <button type="button" className="text-sm text-primary-600 hover:underline" onClick={() => navigate(`/social/platforms/${guide.platformId}`)}>
          Open {guide.label} workspace
        </button>
      </div>
    </div>
  );
}
